import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/free-solid-svg-icons";

/**
 * One colour palette to choose from: a gradient strip, a row of colour dots and
 * the palette's name. Backs the public website's palette picker (HomeSet) and the
 * dashboard's theme palettes (Settings panel).
 *
 * `gradient` (a full CSS background) replaces the two-stop `gradientFrom`/`gradientTo`
 * strip, and `children` are drawn centred on the strip (the custom-colour card's icon).
 *
 * `previewThemeId` scopes a dashboard palette onto the card (`data-theme-preview`),
 * so colours passed as `rgb(var(--color-*))` paint that palette, not the active one.
 *
 * It is a radio: render the cards inside a `role="radiogroup"` that handles
 * `onKeyDown={handlePaletteGroupKeyDown}`, and give only the chosen card `tabIndex={0}`.
 */
export const handlePaletteGroupKeyDown = (event) => {
  // Left/right follow the reading direction, as in any radio group.
  const rtl = getComputedStyle(event.currentTarget).direction === "rtl";
  const step = {
    ArrowDown: 1,
    ArrowUp: -1,
    ArrowRight: rtl ? -1 : 1,
    ArrowLeft: rtl ? 1 : -1,
  }[event.key];
  if (!step) return;

  const radios = [...event.currentTarget.querySelectorAll('[role="radio"]')];
  const index = radios.indexOf(document.activeElement);
  if (index === -1) return;

  event.preventDefault();
  const next = radios[(index + step + radios.length) % radios.length];
  next.focus();
  next.click();
};

const PaletteCard = ({
  name,
  gradientFrom,
  gradientTo,
  swatches,
  isSelected,
  onClick,
  badge,
  previewThemeId,
  tabIndex,
  gradient,
  children,
}) => {
  return (
    <button
      type="button"
      role="radio"
      onClick={onClick}
      aria-checked={isSelected}
      tabIndex={tabIndex}
      data-theme-preview={previewThemeId}
      className={`w-full cursor-pointer rounded-lg border p-3 transition-all duration-200 hover:shadow-md ${
        isSelected
          ? "border-primary bg-primary-tint-bg shadow-sm"
          : "border-border-light bg-surface hover:border-border-muted"
      }`}
    >
      <span
        className="mb-2 flex h-[60px] w-full items-center justify-center rounded-md"
        style={{
          background:
            gradient ||
            `linear-gradient(135deg, ${gradientFrom} 0%, ${gradientFrom} 50%, ${gradientTo} 100%)`,
        }}
      >
        {children}
      </span>

      <span className="flex justify-center gap-1 mb-2">
        {swatches.map((color, index) => (
          <span
            key={`${index}-${color}`}
            className="w-3.5 h-3.5 rounded-full border border-border-light"
            style={{ backgroundColor: color }}
          />
        ))}
      </span>

      <span className="flex flex-wrap items-center justify-center gap-1.5">
        {isSelected && (
          <FontAwesomeIcon
            icon={faCircleCheck}
            className="text-primary text-xs"
            aria-hidden
          />
        )}
        <span className="text-xs font-semibold text-text-strong">{name}</span>
        {badge}
      </span>
    </button>
  );
};

export default PaletteCard;
